import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { searchMovies, sortMovies } from "@/utils/movie";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";

type TypeFilter = "all" | "movie" | "tv";

const TYPE_TABS: { key: TypeFilter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  usePageMeta(
    q ? `搜索“${q}” - 秋霞网影视资源` : "搜索 - 秋霞网影视资源",
    "在秋霞网搜索电影、电视剧资源，支持按类型筛选，快速找到想看的影视作品。"
  );

  const results = useMemo(
    () => searchMovies(q, typeFilter === "all" ? undefined : typeFilter),
    [q, typeFilter]
  );

  const hotMovies = useMemo(() => sortMovies(movies, "views").slice(0, 6), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    setSearchParams({ q: kw });
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
        影视搜索
      </h1>

      <form
        onSubmit={submit}
        className="mt-5 flex flex-col gap-3 sm:flex-row"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-3 focus-within:border-primary-400">
          <i className="ri-search-line text-foreground-400" />
          <input
            type="text"
            name="q"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入片名、导演、演员或类型关键词"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          {keyword && (
            <button
              type="button"
              aria-label="清空"
              onClick={() => setKeyword("")}
              className="text-foreground-400 transition-colors hover:text-foreground-900 cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          搜索
        </button>
      </form>

      {q ? (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              关于 <span className="font-medium text-primary-500">“{q}”</span> 的搜索结果，共
              <span className="mx-1 font-medium text-foreground-950">
                {results.length}
              </span>
              条
            </p>
            <div className="flex items-center gap-1 rounded-full border border-background-300 p-1">
              {TYPE_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setTypeFilter(tab.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors cursor-pointer ${
                    typeFilter === tab.key
                      ? "bg-primary-500 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {results.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-background-300/60 bg-background-100 px-6 py-16 text-center">
              <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                没有找到与“{q}”相关的影视资源
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词试试，或直接浏览下方热门推荐
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="mt-6 rounded-xl border border-background-300/60 bg-background-100 px-6 py-16 text-center">
          <i className="ri-search-eye-line text-5xl text-foreground-400" />
          <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
            输入关键词开始搜索
          </h3>
          <p className="mt-2 text-sm text-foreground-500">
            可搜索片名、导演、主演、类型或上映地区
          </p>
        </div>
      )}

      <section className="mt-14">
        <SectionHeading
          title={results.length > 0 && q ? "你可能喜欢" : "热门推荐"}
          desc="秋霞网高热度影视作品精选"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {hotMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>
    </div>
  );
}